import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Output, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { PageObjectService } from '@Core/models/proprietary/page/page-object.service';
import { ComponentSetting, DesignerComponent, PageSection, PageState } from '@Core/models/proprietary/page/page.state';
import { AlertDialogService } from '@Core/services/proprietary/alert-dialog/alert-dialog.service';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';

@Component({
    standalone: false,
    selector: 'ep-ux-designer-template-panel',
    templateUrl: './ux-designer-template-panel.component.html',
    styleUrls: ['./ux-designer-template-panel.component.css']
})
export class UxDesignerTemplatePanelComponent {
    @ViewChild('templateName') templateName: ElementRef;

    @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent) {
        //console.log(event);
        if (this.model.renameMode && !this.popupOpen) {
            if (event.keyCode === 13 && this.model.New_Title > ' ') {
                //ENTER
                setTimeout(() => {
                    this.endRename();
                }, 1);
            }
            if (event.keyCode === 27) {
                //ESCAPE
                this.cancelRename();
            }
        }
    }

    @Output() onSelectSetting = new EventEmitter<string>();

    @Select(PageState.currentDesignerComponent)
    currentComponent$: Observable<DesignerComponent>;
    currentComponentSub: Subscription;
    currentComponent: DesignerComponent;

    @Select(PageState.currentSection)
    currentSection$: Observable<PageSection>;
    currentSectionSub: Subscription;
    currentSection: PageSection;

    @Select(PageState.currentDesignerComponentSettingList)
    settingList$: Observable<ComponentSetting>;
    settingListSub: Subscription;
    settingList: ComponentSetting[];

    @Select(PageState.dirty)
    pageDirty$: Observable<boolean>;
    pageDirtySub: Subscription;
    pageDirty: boolean = false;

    public model = {
        Setting_ID: null,
        Previous_Setting_ID: null,
        New_Title: '',
        renameMode: false,
        newTemplateSyntax: null,
        settingSelectionMode: false
    };

    popupOpen = false;

    get settingListAsCodeSet(): any {
        return this.settingList.map((x) => {
            return { Code: x.Setting_ID, Code_Desc: x.Setting_Desc };
        });
    }

    get noTemplateSelected(): boolean {
        return this.model.Setting_ID == null || this.model.Setting_ID == 'null';
    }

    constructor(
        public pageOS: PageObjectService,
        public store: Store,
        public app: AppService,
        public location: Location,
        //private confirmationDialog: ConfirmationDialogService
        public cdRef: ChangeDetectorRef,
        public helper: StateHelperService,
        private alertDialog: AlertDialogService
    ) { }

    ngOnInit() {
        this.currentComponentSub = this.currentComponent$.subscribe((x) => {
            let isNewComponentName = false;
            if (x?.name) {
                isNewComponentName = (x?.name || 'NULL') != (this.currentComponent?.name || 'NULL');
            }
            this.currentComponent = x;
            if (isNewComponentName) {
                this.pageOS.loadComponentSettingList(x.name);
            }
        });

        this.currentSectionSub = this.currentSection$.subscribe((x) => {
            this.currentSection = x;
            if (this.model.Setting_ID == '0') {
                //don't get the setting id from state when in New Template mode
                return;
            }

            if (x && x.ComponentSettings) {
                let setting: ComponentSetting = x.ComponentSettings.find(
                    (y) => y.Component_Name == this.currentComponent.name && (!y.Edit_Status || y.Edit_Status != 'D')
                );

                let newSettingID = null;
                if (setting && setting.Setting_ID) {
                    newSettingID = setting.Setting_ID;
                    if (newSettingID == 'null') {
                        newSettingID = null;
                    }
                }

                let settingIDMatch = (newSettingID || 'X') == (this.model.Setting_ID || 'X');

                if (this.model.settingSelectionMode && !settingIDMatch) {
                    // Don't process subscription event if selection change in process and the new setting_id has not yet been set in state
                    return;
                }

                this.model.settingSelectionMode = false; //exit settingSelection mode

                if (settingIDMatch && newSettingID != null) {
                    // Don't process subscription event if old and new setting id match
                    return;
                }

                this.model.Setting_ID = newSettingID || 'null';
                this.autosetEditMode(this.model.Setting_ID);
            } else {
                this.model.Setting_ID = 'null';
            }
        });

        this.settingListSub = this.settingList$.subscribe((x) => {
            //console.log('setting list ' + JSON.stringify(x));
            this.settingList = [
                { Setting_ID: null, Setting_Desc: '<No Template>' },
                // { Setting_ID: '0', Setting_Desc: '<Add New Template>' },
                ...cloneDeep(x)
            ];
            this.cdRef.detectChanges();
        });
    }

    ngOnDestroy() {
        if (this.currentComponentSub) {
            this.currentComponentSub.unsubscribe();
        }
        if (this.currentSectionSub) {
            this.currentSectionSub.unsubscribe();
        }
        if (this.settingListSub) {
            this.settingListSub.unsubscribe();
        }
    }

    settingSelected(settingID: string) {
        this.model.settingSelectionMode = true;
        this.model.Previous_Setting_ID = this.model.Setting_ID;

        if (settingID == 'null') {
            settingID = null;
        }
        if (settingID == '0') {
            this.model.Setting_ID = '0';
            this.startRename('NEW');
            return;
        }

        //used to help state change subscribers to avoid refresh until all changes have been made
        this.pageOS.setPendingDesignerRuleChange();

        this.autosetEditMode(settingID);

        this.pageOS.updateSectionComponentSettingLink(null, {
            Component_Name: this.currentComponent.name,
            Setting_ID: settingID
        });
    }

    startRename(mode: 'NEW' | 'CLONE' | 'EDIT') {
        this.pageOS.setCurrentDesignerComponentEditMode(false);
        this.model.newTemplateSyntax = {};
        if (mode == 'CLONE') {
            this.model.Previous_Setting_ID = this.model.Setting_ID;
            this.model.Setting_ID = '0';
            if (this.currentSection?.Ux_Config) {
                this.model.newTemplateSyntax = cloneDeep(this.currentSection.Ux_Config[this.currentComponent.name] || {});
                delete this.model.newTemplateSyntax.Setting_ID;
            }
        }

        if (mode == 'EDIT') {
            this.model.Previous_Setting_ID = this.model.Setting_ID;
        }

        if (!this.model.Setting_ID || this.model.Setting_ID == '0') {
            this.model.New_Title = '';
        } else {
            let codeNode = this.settingListAsCodeSet.find((x) => x.Code == this.model.Setting_ID);
            if (codeNode) {
                this.model.New_Title = codeNode.Code_Desc;
            } else {
                this.model.New_Title = '?';
            }
        }

        this.model.renameMode = true;
        this.autosetEditMode(this.model.Setting_ID);
        setTimeout(() => {
            if (this.templateName) {
                this.templateName.nativeElement.focus();
            }
        }, 10);
    }

    endRename() {
        // if setting name already used then stop endRename and return
        let foundView = this.settingListAsCodeSet.find(
            (x) => x.Code_Desc.toLowerCase().trim() == this.model.New_Title.toLowerCase().trim()
        );
        if (foundView && (foundView.Code != this.model.Setting_ID || this.model.Setting_ID == '0')) {
            this.popupOpen = true;
            this.alertDialog
                .showAlert(
                    'Duplicate Template Name',
                    'The template named entered is already in use.  Please enter a unique template name.',
                    'OK'
                )
                .then(
                    (x) => {
                        this.popupOpen = false;
                    },
                    (x) => {
                        this.popupOpen = false;
                    }
                );
            return;
        }

        let node: ComponentSetting = null;
        if (this.model.Setting_ID == '0') {
            //this is a new template
            let newID = this.helper.getTempID();
            node = {
                Setting_ID: newID,
                Setting_Desc: this.model.New_Title,
                Component_Name: this.currentComponent.name,
                Setting_Syntax: this.model.newTemplateSyntax
            };
            this.settingList.push(node);
            this.model.Setting_ID = newID;
            this.pageOS.updateComponentSetting(node);
            this.pageOS.updateSectionComponentSettingLink(null, node);
        } else {
            node = this.settingList.find((x) => x.Setting_ID == this.model.Setting_ID);
            if (node) {
                node.Setting_Desc = this.model.New_Title;
            }
            this.pageOS.updateComponentSetting(node);
        }

        this.model.renameMode = false;
        this.model.Previous_Setting_ID = this.model.Setting_ID;
        this.autosetEditMode(this.model.Setting_ID);
    }

    cancelRename() {
        this.model.renameMode = false;
        this.model.Setting_ID = this.model.Previous_Setting_ID;
        this.autosetEditMode(this.model.Setting_ID);
    }

    clone() { }

    autosetEditMode(settingID: string) {
        this.onSelectSetting.emit(settingID);
        this.pageOS.setCurrentDesignerComponentEditMode(
            settingID != null && settingID != 'null' && settingID != '0' && !this.model.renameMode
        );
    }
}
